import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box, Paper, TextField, IconButton, Typography, CircularProgress,
  Chip, Avatar, Divider
} from '@mui/material';
import {
  SendOutlined, SmartToyOutlined, PersonOutlined, RestartAltOutlined
} from '@mui/icons-material';
import { updateScreenTitle } from '../../../LayoutContainers/AppLayout/redux/AppSlice';
import AgentService from '../../../services/AgentService';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const ADMIN_QUESTIONS = [
  "How many customer organizations are there?",
  "Show all pending tickets across all customers",
  "Who are the top spending customers?",
  "What's the total platform revenue?",
  "Show customer wallet balances",
];

const CUSTOMER_QUESTIONS = [
  "How many visas expire this month?",
  "Show all pending tickets",
  "What did we spend on visa processing?",
  "Which employees are missing documents?",
  "What's our compliance status?",
];

const AgentChat = () => {
  const dispatch = useDispatch();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEnabled, setIsEnabled] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    dispatch(updateScreenTitle('AI Assistant'));
    checkConfig();
    const sid = initSession();
    detectRole();
    if (sid) loadHistory(sid);
    return () => dispatch(updateScreenTitle(''));
  }, [dispatch]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const detectRole = () => {
    try {
      const authInfo = JSON.parse(localStorage.getItem('authInfo') || '{}');
      const userType = authInfo?.principalInfo?.userType || '';
      const roles = authInfo?.principalInfo?.roles || [];
      setIsAdmin(userType === 'ADMIN_ORG' || roles.some(r => r.includes('ROOT')));
    } catch (e) {
      setIsAdmin(false);
    }
  };

  const checkConfig = async () => {
    try {
      const response = await AgentService.getConfig();
      setIsEnabled(response?.enabled ?? false);
    } catch (error) {
      setIsEnabled(false);
    }
  };

  const initSession = () => {
    let storedSessionId = sessionStorage.getItem('agentSessionId');
    if (!storedSessionId) {
      storedSessionId = crypto.randomUUID();
      sessionStorage.setItem('agentSessionId', storedSessionId);
    }
    setSessionId(storedSessionId);
    return storedSessionId;
  };

  const loadHistory = async (sid) => {
    try {
      const history = await AgentService.getHistory(sid);
      if (Array.isArray(history) && history.length > 0) {
        const restored = [];
        history.forEach(conv => {
          if (conv.userMessage) restored.push({ role: 'user', content: conv.userMessage });
          if (conv.response) restored.push({ role: 'assistant', content: conv.response });
        });
        setMessages(restored);
      }
    } catch (e) {
      // No history or error — start fresh
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async (text) => {
    const message = text || input.trim();
    if (!message || isLoading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: message }]);
    setIsLoading(true);

    try {
      const response = await AgentService.chat(sessionId, message);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response?.response || 'No response received.',
        toolsUsed: response?.toolsUsed || [],
      }]);
    } catch (error) {
      const status = error?.response?.status;
      let errorMsg = error?.response?.data?.message ||
                     error?.response?.data?.error ||
                     'Something went wrong. Please try again.';

      if (status === 429) {
        errorMsg = 'You have reached your daily AI Assistant usage limit. Please try again tomorrow or contact Thaklis support to extend your limit.';
      } else if (status === 403) {
        errorMsg = 'AI Assistant is not enabled for your organization. Please contact Thaklis support.';
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: errorMsg,
        isError: true,
      }]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewSession = () => {
    const newSessionId = crypto.randomUUID();
    sessionStorage.setItem('agentSessionId', newSessionId);
    setSessionId(newSessionId);
    setMessages([]);
  };

  if (isEnabled === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isEnabled === false) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <SmartToyOutlined sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            AI Assistant is not enabled for your organization
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
            Contact Thaklis support to enable this feature.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const suggestedQuestions = isAdmin ? ADMIN_QUESTIONS : CUSTOMER_QUESTIONS;

  return (
    <Box sx={{ p: { xs: 0.5, sm: 1, md: 2 }, height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      <Paper sx={{
        flex: 1, display: 'flex', flexDirection: 'column',
        overflow: 'hidden', borderRadius: { xs: 0, sm: 2 }, boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
      }}>
        {/* Header */}
        <Box sx={{
          px: { xs: 1.5, sm: 2.5 }, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: { xs: 28, sm: 34 }, height: { xs: 28, sm: 34 } }}>
              <SmartToyOutlined sx={{ fontSize: { xs: 16, sm: 20 } }} />
            </Avatar>
            <Box>
              <Typography variant="subtitle2" fontWeight={600} fontSize={{ xs: '0.8rem', sm: '0.875rem' }}>
                Thaklis AI Assistant
              </Typography>
              <Typography variant="caption" color="text.secondary" fontSize="0.65rem"
                sx={{ display: { xs: 'none', sm: 'block' } }}>
                {isAdmin ? 'Platform Admin Mode' : 'Ask anything about your organization'}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={handleNewSession} title="New conversation"
            sx={{ color: 'text.secondary' }}>
            <RestartAltOutlined fontSize="small" />
          </IconButton>
        </Box>

        {/* Messages */}
        <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 1, sm: 2, md: 2.5 }, bgcolor: '#fafafa' }}>
          {messages.length === 0 && (
            <Box sx={{ textAlign: 'center', mt: { xs: 3, sm: 6 } }}>
              <SmartToyOutlined sx={{ fontSize: { xs: 36, sm: 44 }, color: 'text.disabled', mb: 1.5 }} />
              <Typography variant="subtitle1" fontWeight={600} color="text.primary" gutterBottom
                fontSize="0.95rem">
                How can I help you today?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }} fontSize="0.8rem">
                {isAdmin
                  ? 'Ask about customer organizations, tickets, revenue, payments, and platform operations.'
                  : 'Ask about your employees, visas, tickets, spending, or compliance.'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center', maxWidth: 600, mx: 'auto', px: { xs: 1, sm: 0 } }}>
                {suggestedQuestions.map((q, i) => (
                  <Chip
                    key={i}
                    label={q}
                    variant="outlined"
                    onClick={() => handleSend(q)}
                    size="small"
                    sx={{
                      cursor: 'pointer',
                      fontSize: { xs: '0.65rem', sm: '0.75rem' },
                      height: { xs: 26, sm: 30 },
                      borderColor: 'divider',
                      '&:hover': { bgcolor: 'primary.main', color: 'white', borderColor: 'primary.main' }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {messages.map((msg, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                gap: 1.5,
                mb: 2,
                flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
              }}
            >
              <Avatar sx={{
                width: { xs: 24, sm: 30 }, height: { xs: 24, sm: 30 }, mt: 0.5,
                bgcolor: msg.role === 'user' ? 'primary.main' : '#e8e8e8',
                color: msg.role === 'user' ? 'white' : 'text.primary',
                display: { xs: 'none', sm: 'flex' },
              }}>
                {msg.role === 'user'
                  ? <PersonOutlined sx={{ fontSize: 16 }} />
                  : <SmartToyOutlined sx={{ fontSize: 16 }} />}
              </Avatar>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  maxWidth: { xs: '92%', sm: '75%' },
                  bgcolor: msg.role === 'user' ? 'primary.main' : 'white',
                  color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                  border: msg.role === 'user' ? 'none' : '1px solid',
                  borderColor: 'divider',
                  borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  fontSize: '0.825rem',
                  lineHeight: 1.6,
                  '& table': {
                    borderCollapse: 'collapse', width: '100%', my: 1.5, fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    display: 'block', overflowX: 'auto', whiteSpace: 'nowrap',
                  },
                  '& th, & td': {
                    border: '1px solid', borderColor: 'divider', px: 1, py: 0.5, textAlign: 'left',
                  },
                  '& th': { bgcolor: '#f5f5f5', fontWeight: 600, fontSize: '0.75rem' },
                  '& p': { m: 0, mb: 0.75, fontSize: '0.825rem' },
                  '& p:last-child': { mb: 0 },
                  '& ul, & ol': { pl: 2.5, my: 0.5, fontSize: '0.825rem' },
                  '& li': { mb: 0.25 },
                  '& code': {
                    bgcolor: '#f0f0f0', px: 0.5, py: 0.25, borderRadius: 0.5, fontSize: '0.8em',
                    fontFamily: 'monospace',
                  },
                  '& strong': { fontWeight: 600 },
                  '& h3, & h4': { fontSize: '0.85rem', fontWeight: 600, mt: 1, mb: 0.5 },
                }}
              >
                {msg.role === 'user' ? (
                  <Typography variant="body2" fontSize="0.825rem">{msg.content}</Typography>
                ) : (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                )}
              </Paper>
            </Box>
          ))}

          {isLoading && (
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <Avatar sx={{ width: 30, height: 30, bgcolor: '#e8e8e8', color: 'text.primary', display: { xs: 'none', sm: 'flex' } }}>
                <SmartToyOutlined sx={{ fontSize: 16 }} />
              </Avatar>
              <Paper elevation={0} sx={{
                p: 1.5, bgcolor: 'white', border: '1px solid', borderColor: 'divider',
                borderRadius: '16px 16px 16px 4px',
                display: 'flex', alignItems: 'center', gap: 1
              }}>
                <CircularProgress size={14} />
                <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                  Thinking...
                </Typography>
              </Paper>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Input */}
        <Divider />
        <Box sx={{ p: { xs: 1, sm: 1.5 }, display: 'flex', gap: 0.75, alignItems: 'flex-end', bgcolor: 'white' }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={3}
            size="small"
            placeholder={isAdmin
              ? "Ask about customers, tickets, revenue..."
              : "Ask about employees, visas, tickets..."}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            inputProps={{ maxLength: 2000, style: { fontSize: '0.825rem' } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                bgcolor: '#f8f8f8',
                fontSize: { xs: '0.8rem', sm: '0.825rem' },
              }
            }}
          />
          <IconButton
            color="primary"
            onClick={() => handleSend()}
            disabled={!input.trim() || isLoading}
            sx={{
              bgcolor: 'primary.main', color: 'white',
              width: { xs: 34, sm: 36 }, height: { xs: 34, sm: 36 },
              minWidth: { xs: 34, sm: 36 },
              '&:hover': { bgcolor: 'primary.dark' },
              '&.Mui-disabled': { bgcolor: 'grey.200', color: 'grey.400' }
            }}
          >
            <SendOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Paper>
    </Box>
  );
};

export default AgentChat;
